<template>
    <transition name="fade">
        <div
            v-if="isOpen"
            class="d-modal"
        >
            <section
                class="modal-box"
                :class="{'big': isBig, 'hasMessage': !!message }"
            >
                <header
                    :class="{'isError': isError}"
                >
                    <DIcon
                        v-if="isError"
                        name="alert"
                    />

                    <h1>{{ title }}</h1>
                    <DButton
                        icon="close"
                        mini
                        @click="isConfirm ? dismiss() : close(true)"
                    />
                </header>

                <p v-if="message">
                    {{ message }}
                </p>

                <main
                    v-if="hasSlot"
                >
                    <slot />
                </main>

                <div
                    v-if="isConfirm"
                    class="actions"
                >
                    <DButton
                        :label="$t('common.confirm')"
                        :color="isError ? 'danger' : 'primary'"
                        @click="confirm"
                    />
                    <DButton
                        :label="$t('common.close')"
                        color="secondary"
                        @click="dismiss"
                    />
                </div>
                <div
                    v-if="!isConfirm"
                    class="actions"
                >
                    <slot name="buttons" />
                    <DButton
                        :label="$t('common.cancel')"
                        :color="isError ? 'danger' : 'secondary'"
                        @click="close(true)"
                    />
                </div>
            </section>
        </div>
    </transition>
</template>

<script>
export default {
    name: 'DModal',
    props: {
        title: {
            type: String,
            required: true
        },
        message: {
            type: String,
            default: '',
            required: false
        },
        isError: {
            type: Boolean,
            required: false,
            default: false
        },
        isConfirm: {
            type: Boolean,
            required: false,
            default: false
        },
        isBig: {
            type: Boolean,
            required: false,
            default: false
        },
        isSmall: {
            type: Boolean,
            required: false,
            default: false
        },
        isMedium: {
            type: Boolean,
            required: false,
            default: false
        },
        isAwaitingValidation: {
            type: Boolean,
            required: false,
            default: false
        },
        show: {
            type: Boolean,
            required: true,
            default: false
        },
        closeColorSecondary: {
            type: Boolean,
            required: false,
            default: false
        },
        showChangeReasonInput: {
            type: Boolean,
            default: false
        },
        confirmClose: {
            type: Boolean,
            default: false
        },
        confirmCloseText: {
            type: String,
            default: ''
        },
        overflowAuto: {
            type: Boolean,
            default: false
        }
    },
    emits: ['confirm', 'dismiss', 'open', 'close', 'clearFields', 'update:modelValue'],
    data () {
        return {
            isOpen: false,
            changeReason: '',
            showCloseConfirmation: false
        }
    },
    computed: {
        hasSlot () {
            return Object.keys(this.$slots).length !== 0
        },
        propErrors () {
            return this.$page.props.errors
        }
    },
    watch: {
        show: {
            immediate: true,
            handler (show) {
                if (show) {
                    this.open()
                } else {
                    this.close()
                }
            }
        }
    },
    created () {
        window.addEventListener('keydown', this.handleKeys)
    },
    unmounted () {
        window.removeEventListener('keydown', this.handleKeys)
    },
    methods: {
        confirm () {
            this.$emit('confirm')
            if (!this.isAwaitingValidation) {
                this.close(false)
            }
        },
        dismiss () {
            this.$emit('dismiss')
            this.changeReason = ''
            this.close(true)
        },
        open () {
            this.isOpen = true
            this.$emit('open')
        },
        close (clearFields = false, closeConfirmed = false) {
            if (this.confirmClose === true && closeConfirmed === false) {
                this.showCloseConfirmation = true

                return
            }

            this.isOpen = false
            this.$emit('close')
            this.changeReason = ''
            if (clearFields) {
                this.$emit('clearFields')
            }
        },
        handleKeys (event) {
            // ESC Key
            if (event.keyCode === 27) {
                if (this.isConfirm) {
                    this.dismiss()
                } else {
                    this.close(true)
                }
            }
        },
        updateValue () {
            this.$emit('update:modelValue', this.changeReason)
        }
    }
}
</script>

<style lang="scss" scoped>
.d-modal {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(5px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 5000;
    padding: 50px;
    pointer-events: all !important;

    & .modal-box {
        background: var(--colorAppSurface);
        display: grid;
        width: 90%;
        max-width: 520px;
        border-radius: 5px;
        max-height: 100%;
        overflow: hidden;

        & header {
            display: grid;
            grid-template-columns: 1fr auto;
            grid-gap: 15px;
            padding: 15px 20px;
            align-items: center;
            border-bottom: 1px solid var(--colorAppBorder);

            &.isError {
                grid-template-columns: auto 1fr auto;
                background: var(--colorDanger);
                color: #fff;
            }

            & .mdi {
                opacity: 0.6;
            }
            & h1 {
                font-size: 18px;
                margin: 0;
            }
        }
        & p {
            padding: 25px 20px;
            line-height: 1.5em;
            margin: 0;
            opacity: 0.6;
        }
        & .actions {
            border-top: 1px solid var(--colorAppBorder);
            padding: 15px 20px;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            grid-gap: 10px;
        }

        & main, & p {
            overflow-x: auto;
            overflow-y: auto;
            width: 100%;
            max-height: calc(100vh - 230px);
        }

        &.big {
            max-width: 960px;
        }

        &.small {
            max-width: 300px;
        }

        &.medium {
            max-width: 700px;
        }

    }
    &.dropdown-overflow-visible{
        overflow: hidden;
        & main, & p {
            overflow-x: visible;
            overflow-y: visible;
        }
    }
}

.overflow-auto {
    overflow: auto !important;
}

.fade-enter-active, .fade-leave-active {
    transition: opacity .1s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
}
</style>
